import { Badge } from '@/components/ui/badge'
import type { TicketStatus, TicketPriority, TicketSentiment } from '@/types/entities'
import { getTicketStatusLabel, getTicketStatusVariant, getIntentLabel, getSentimentLabel, getPriorityLabel } from '../hooks/useSupport'

interface TicketStatusBadgeProps {
  status: TicketStatus
}

export function TicketStatusBadge({ status }: TicketStatusBadgeProps) {
  return (
    <Badge variant={getTicketStatusVariant(status)}>
      {getTicketStatusLabel(status)}
    </Badge>
  )
}

interface PriorityBadgeProps {
  priority: TicketPriority
}

export function PriorityBadge({ priority }: PriorityBadgeProps) {
  const colorMap: Record<TicketPriority, string> = {
    low: 'bg-slate-100 text-slate-700',
    normal: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700',
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorMap[priority]}`}>
      {getPriorityLabel(priority)}
    </span>
  )
}

interface IntentBadgeProps {
  intent: string | null
}

export function IntentBadge({ intent }: IntentBadgeProps) {
  if (!intent) return null
  return (
    <Badge variant="outline" className="text-xs">
      {getIntentLabel(intent)}
    </Badge>
  )
}

interface SentimentBadgeProps {
  sentiment: TicketSentiment | null
}

export function SentimentBadge({ sentiment }: SentimentBadgeProps) {
  if (!sentiment) return null

  const colorMap: Record<string, string> = {
    positive: 'bg-green-100 text-green-700',
    neutral: 'bg-slate-100 text-slate-600',
    negative: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700',
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorMap[sentiment] || ''}`}>
      {getSentimentLabel(sentiment)}
    </span>
  )
}

interface ConfidenceBadgeProps {
  confidence: number | null
}

export function ConfidenceBadge({ confidence }: ConfidenceBadgeProps) {
  if (confidence === null || confidence === undefined) return null

  const pct = Math.round(confidence * 100)
  const color = pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-yellow-600' : 'text-red-600'

  return (
    <span className={`text-xs font-mono ${color}`}>
      {pct}%
    </span>
  )
}
